import React, { useState, useEffect, useCallback } from 'react';
import { Mic, MicOff, Volume2, AlertCircle } from 'lucide-react';

interface VoiceSearchProps {
  onSearch: (query: string) => void;
  placeholder?: string;
}

export default function VoiceSearch({ onSearch, placeholder = 'Search by voice...' }: VoiceSearchProps) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const [synthesis, setSynthesis] = useState<SpeechSynthesis | null>(null);
  const [isSupported, setIsSupported] = useState(true);

  // Initialize Web Speech API
  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      setRecognition(recognitionInstance);
      setSynthesis(window.speechSynthesis);
    } else {
      setIsSupported(false);
      setError('Voice search is not supported in this browser');
    }
  }, []);

  // Handle speech recognition results
  const handleResult = useCallback((event: SpeechRecognitionEvent) => {
    const transcript = Array.from(event.results)
      .map(result => result[0].transcript)
      .join('');
    
    setTranscript(transcript);
    
    // If this is a final result, trigger the search
    if (event.results[0].isFinal) {
      onSearch(transcript);
      setIsListening(false);
    }
  }, [onSearch]);

  // Handle speech recognition errors
  const handleError = useCallback((event: SpeechRecognitionErrorEvent) => {
    console.error('Speech recognition error:', event.error);
    
    let errorMessage = 'An error occurred with voice search';
    
    switch (event.error) {
      case 'not-allowed':
        errorMessage = 'Microphone access was denied. Please allow microphone access to use voice search.';
        break;
      case 'no-speech':
        errorMessage = 'No speech was detected. Please try again.';
        break;
      case 'network':
        errorMessage = 'Network error occurred. Please check your connection.';
        break;
      case 'aborted':
        errorMessage = 'Voice search was aborted.';
        break;
      default:
        errorMessage = `Error: ${event.error}`;
    }
    
    setError(errorMessage);
    setIsListening(false);
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (recognition) {
      recognition.onresult = handleResult;
      recognition.onerror = handleError;
      recognition.onend = () => setIsListening(false);
    }
  }, [recognition, handleResult, handleError]);

  // Provide voice feedback
  const speak = (text: string) => {
    if (synthesis) {
      // Cancel any ongoing speech
      synthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 1.1; // Slightly faster than normal
      utterance.pitch = 1.0; // Normal pitch
      synthesis.speak(utterance);
    }
  };

  const toggleListening = () => {
    if (!isSupported) {
      setError('Voice search is not supported in your browser');
      return;
    }
    
    if (!recognition) {
      setError('Voice search is not initialized');
      return;
    }

    if (isListening) {
      recognition.stop();
      speak('Voice search stopped');
    } else {
      setError(null);
      setTranscript('');
      
      try {
        recognition.start();
        speak('Listening for AI training search...');
      } catch (err) {
        console.error('Error starting speech recognition:', err);
        setError('Could not start voice search. Please try again.');
        return;
      }
    }
    setIsListening(!isListening);
  };

  // Add voice search commands for AI training
  const voiceSearchCommands = [
    "Try saying: 'AI training in Omaha'",
    "Try saying: 'ChatGPT courses'",
    "Try saying: 'Microsoft Copilot workshops'",
    "Try saying: 'AI for business'"
  ];
  
  // Randomly select a command suggestion
  const randomCommandIndex = Math.floor(Math.random() * voiceSearchCommands.length);

  return (
    <div className="relative">
      <div className="flex items-center space-x-2">
        <button
          onClick={toggleListening}
          className={`p-2 rounded-full transition-colors duration-200 ${
            isListening 
              ? 'bg-red-100 text-red-600 hover:bg-red-200' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={isListening ? 'Stop listening' : 'Start voice search'}
          aria-label={isListening ? 'Stop voice search' : 'Start voice search'}
          disabled={!isSupported}
        >
          {isListening ? (
            <MicOff className="h-5 w-5" />
          ) : (
            <Mic className="h-5 w-5" />
          )}
        </button>
        <div className="relative flex-grow">
          <input
            type="text"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder={isSupported ? voiceSearchCommands[randomCommandIndex] : "Voice search not supported in this browser"}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F98B3D] focus:border-transparent"
            aria-label="Voice search input"
            disabled={!isSupported}
          />
          {isListening && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <Volume2 className="h-5 w-5 text-[#F98B3D] animate-pulse" />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="absolute top-full left-0 right-0 mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center text-red-600 text-sm">
            <AlertCircle className="h-4 w-4 mr-1" />
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
}